export type TokenRefreshStatus = "refreshed" | "invalid" | "transient";

export type TokenRefreshResult = {
  status: TokenRefreshStatus;
};

let refreshInFlight: Promise<TokenRefreshResult> | null = null;

export function clearSessionTokens(): void {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("managerName");
}

export function clearSessionAndRedirectToLogin(): void {
  clearSessionTokens();
  window.location.href = "/login";
}

/**
 * Refresh the JWT access token.
 *
 * - refreshed: new access token stored
 * - invalid: refresh token missing/rejected — safe to log the user out
 * - transient: network/5xx/etc — keep existing tokens and retry later
 */
export async function refreshAccessToken(): Promise<TokenRefreshResult> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = doRefreshAccessToken().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function doRefreshAccessToken(): Promise<TokenRefreshResult> {
  const refresh = localStorage.getItem("refreshToken");
  if (!refresh) {
    return { status: "invalid" };
  }

  let baseUrl = process.env.REACT_APP_SERVER_URL || "";
  if (baseUrl.endsWith("/")) {
    baseUrl = baseUrl.slice(0, -1);
  }

  try {
    const res = await fetch(`${baseUrl}/auth/refresh/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh }),
    });

    // Explicit auth rejection from the refresh endpoint.
    if (res.status === 401 || res.status === 403) {
      return { status: "invalid" };
    }

    if (!res.ok) {
      console.error("Token refresh failed with status:", res.status);
      return { status: "transient" };
    }

    const data = await res.json();
    if (!data?.access || typeof data.access !== "string") {
      console.error("Token refresh response missing access token");
      return { status: "transient" };
    }

    // If the user logged out or the refresh token was rotated while this
    // request was in flight, do not write a stale access token back.
    if (localStorage.getItem("refreshToken") !== refresh) {
      return { status: "transient" };
    }

    localStorage.setItem("accessToken", data.access);
    return { status: "refreshed" };
  } catch (err) {
    console.error("Token refresh failed:", err);
    return { status: "transient" };
  }
}

export function isAuthWebSocketClose(event: {
  code: number;
  reason?: string;
}): boolean {
  const reason = (event.reason || "").toLowerCase();
  return (
    event.code === 4001 ||
    reason.includes("token") ||
    reason.includes("authentication")
  );
}

/** Test helper — clears the single-flight lock between cases. */
export function resetRefreshInFlightForTests(): void {
  refreshInFlight = null;
}
